import { useBudgetSignatures } from "@/hooks/useBudgetSignatures";
import { useReprocessBudgetPdf } from "@/hooks/useBudgets";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Clock,
  XCircle,
  FileSignature,
  Pencil,
  Type,
  ShieldCheck,
  Download,
  Eye,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { SIGNATURE_STATUS_META, TONE_SOFT_CLASS } from "@/lib/statusTone";

interface Props {
  budgetId: string;
  signedPdfUrl?: string | null;
  finalizedAt?: string | null;
}

const statusIcon: Record<keyof typeof SIGNATURE_STATUS_META, typeof Clock> = {
  pending: Clock,
  expired: AlertTriangle,
  signed: CheckCircle2,
  rejected: XCircle,
};

const isExpired = (s: { status: string; expires_at?: string | null }) =>
  s.status === "pending" && !!s.expires_at && new Date(s.expires_at).getTime() < Date.now();

const typeIcon = {
  drawn: Pencil,
  typed: Type,
  certificate: ShieldCheck,
};

const typeLabel = {
  drawn: "Desenhada",
  typed: "Digitada",
  certificate: "Certificado digital",
};

export function BudgetSignatureTimeline({ budgetId, signedPdfUrl, finalizedAt }: Props) {
  const { data: sigs, isLoading } = useBudgetSignatures(budgetId);
  const reprocess = useReprocessBudgetPdf();

  if (isLoading || !sigs || sigs.length === 0) return null;

  const allSigned = sigs.every((s) => s.status === "signed");
  const stuck = allSigned && !signedPdfUrl;

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Linha do tempo de assinaturas
          </p>
        </div>
        {signedPdfUrl && (
          <div className="flex items-center gap-2">
            <StatusBadge shape="tag" tone="ok">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              Documento finalizado
            </StatusBadge>
            <Button asChild size="sm" variant="outline" className="h-7 gap-1 text-xs">
              <a href={signedPdfUrl} target="_blank" rel="noopener noreferrer" download>
                <Download className="h-3 w-3" />
                Baixar PDF Assinado
              </a>
            </Button>
          </div>
        )}
        {stuck && (
          <div className="flex items-center gap-2">
            <StatusBadge shape="tag" tone="bad">
              <AlertTriangle className="mr-1 h-3 w-3" />
              PDF final não foi gerado
            </StatusBadge>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              disabled={reprocess.isPending}
              onClick={() => reprocess.mutate(budgetId)}
            >
              {reprocess.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Reprocessar PDF assinado
            </Button>
          </div>
        )}
      </div>
      {stuck && (
        <p className="rounded border border-bad/30 bg-bad-soft p-2 text-xs text-bad">
          Todos assinaram, mas a geração do PDF final falhou — o cliente ainda não recebeu o
          documento assinado por e-mail. Clique em "Reprocessar" para tentar de novo.
        </p>
      )}
      <div className="space-y-2">
        {sigs.map((s, idx) => {
          const key = (isExpired(s) ? "expired" : s.status) as keyof typeof SIGNATURE_STATUS_META;
          const meta = SIGNATURE_STATUS_META[key] ?? SIGNATURE_STATUS_META.pending;
          const Icon = statusIcon[key] ?? Clock;
          const TIcon = s.signature_type ? typeIcon[s.signature_type] : null;
          return (
            <div key={s.id} className="flex items-start gap-3 rounded-lg border bg-card p-3">
              <div
                className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${TONE_SOFT_CLASS[meta.tone]}`}
              >
                {idx + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{s.signer_name}</span>
                  <StatusBadge shape="tag" tone={meta.tone}>
                    <Icon className="mr-1 h-3 w-3" />
                    {meta.label}
                  </StatusBadge>
                  {s.signature_type && TIcon && (
                    <Badge variant="outline" className="text-xs">
                      <TIcon className="mr-1 h-3 w-3" />
                      {typeLabel[s.signature_type]}
                    </Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{s.signer_email}</div>
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {s.sent_at && (
                    <div>
                      Enviado{" "}
                      {formatDistanceToNow(new Date(s.sent_at), { addSuffix: true, locale: ptBR })}
                    </div>
                  )}
                  {/* Old rows signed before this audit trail existed have no
                      viewed_at — that's expected, not an anomaly, so this
                      step simply doesn't render rather than showing broken
                      or placeholder text. */}
                  {s.viewed_at && (
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3 shrink-0" />
                      <span>
                        Visualizado{" "}
                        {formatDistanceToNow(new Date(s.viewed_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                        {s.viewed_ip && ` · IP ${s.viewed_ip}`}
                      </span>
                    </div>
                  )}
                  {s.signed_at && (
                    <div>
                      Assinado{" "}
                      {formatDistanceToNow(new Date(s.signed_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </div>
                  )}
                  {s.rejected_at && (
                    <div>
                      Rejeitado{" "}
                      {formatDistanceToNow(new Date(s.rejected_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </div>
                  )}
                  {isExpired(s) && (
                    <div className="text-notice">
                      Expirou{" "}
                      {formatDistanceToNow(new Date(s.expires_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}{" "}
                      — use "Reenviar para Assinatura" para gerar um novo link.
                    </div>
                  )}
                </div>
                {s.rejection_reason && (
                  <p className="mt-1 rounded border border-bad/30 bg-bad-soft p-2 text-xs text-bad">
                    Motivo: {s.rejection_reason}
                  </p>
                )}
                {s.signature_type === "drawn" && s.signature_data && (
                  <img
                    src={s.signature_data}
                    alt="assinatura"
                    className="mt-2 max-h-16 rounded border bg-card"
                  />
                )}
                {s.signature_type === "typed" && s.signature_data && (
                  <p
                    className="mt-2 text-lg italic"
                    style={{ fontFamily: "'Brush Script MT', cursive" }}
                  >
                    {s.signature_data}
                  </p>
                )}
                {s.signature_type === "certificate" && s.certificate_info && (
                  <div className="mt-2 rounded bg-muted/40 p-2 text-xs">
                    <p>
                      <strong>CN:</strong>{" "}
                      {String((s.certificate_info as { cn?: string }).cn || "—")}
                    </p>
                    <p>
                      <strong>Emissor:</strong>{" "}
                      {String((s.certificate_info as { issuer?: string }).issuer || "—")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
